import { Input as BaseInput } from '@base-ui/react/input'
import { useMergedRef } from '@eigtech/ui/hooks/use-merged-ref'
import { cn } from '@eigtech/ui/lib/utils'
import { type VariantProps } from 'class-variance-authority'
import type { FactoryArg } from 'imask'
import * as React from 'react'
import { IMaskMixin, type IMaskInputProps } from 'react-imask'

import { inputVariants } from './input'

type BaseInputProps = React.ComponentProps<typeof BaseInput>

export type MaskedInputProps = BaseInputProps &
  VariantProps<typeof inputVariants> &
  Partial<Omit<IMaskInputProps<HTMLInputElement>, 'mask'>> & {
    mask?: FactoryArg
  }

function IMaskBaseInputInner(
  props: IMaskInputProps<HTMLInputElement> &
    VariantProps<typeof inputVariants> & { inputRef: React.Ref<HTMLInputElement | null> }
) {
  const { inputRef, className, variant, ...rest } = props

  return (
    <BaseInput
      ref={inputRef}
      className={cn(inputVariants({ variant }), className)}
      data-slot="input"
      {...(rest as BaseInputProps)}
    />
  )
}

const IMaskBaseInput = IMaskMixin(IMaskBaseInputInner)

const MaskedInput: React.ForwardRefExoticComponent<
  MaskedInputProps & React.RefAttributes<HTMLInputElement>
> = React.forwardRef<HTMLInputElement, MaskedInputProps>(function MaskedInput(
  { inputRef, className, variant, ...props },
  ref
) {
  const mergedInputRef = useMergedRef(inputRef, ref)

  return (
    <IMaskBaseInput {...props} className={className} inputRef={mergedInputRef} variant={variant} />
  )
})

export { MaskedInput }
